/** /bookings
 *
 * Ops-wide booking search across tenants, driven by the reusable QuerySearch
 * query-builder widget. The serialized query lives in `?q=`; a tenant picker
 * (`?tenant=12,845`, one or several operators) locks the query's tenant and
 * injects `tour_operator_id`. Server-side pagination + sorting; all column
 * filtering is delegated to the widget (grid `disableColumnFilter`).
 */
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as bookingsApi from '@/api/bookings';
import type { BookingListItem, BookingTag } from '@/api/bookings';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import type { BatchActionDef, BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { countLabelDisplayedRows } from '@/components/DataGrid/CountTotalLabel';
import { useCountMode } from '@/components/DataGrid/useCountMode';
import { useGridCountMode } from '@/hooks/useUserPreferences';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { EmitEventDialog } from '@/features/emit-events/EmitEventDialog';
import { BookingPanelSummary } from '@/features/bookings/overview/BookingPanelSummary';
import { entityExportBatchAction } from '@/features/exports/EntityExportDialog';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  resolveConditionOperator,
  serialize,
  toWireAst,
  type FilterNode,
  type FilterValue,
  type QueryOperator,
  type WireNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { formatTenantIds, singleTenantId } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { ChangeOwnerDialog } from './ChangeOwnerDialog';
import { EditBookingsDialog } from './edit/EditBookingsDialog';
import { ManageTagsDialog } from './ManageTagsDialog';
import { ReimportXmlDialog } from './ReimportXmlDialog';
import { BOOKED_STATE_LABEL, buildBookingNamespaces } from './bookingsNamespaces';
import {
  buildColumns,
  expansionsForColumns,
  type ReplaceFilterCond,
  type ReplaceableNamespace,
  type ToggleableNamespace,
} from './bookingsColumns';

const DEFAULT_PAGE_SIZE = 50;
const EMBEDDED_PAGE_SIZE = 25;

const DEFAULT_HIDDEN = [
  'custom_id',
  'alt_custom_id',
  'order_ref',
  'departure_key',
  'supplier_ref',
  'reseller_ref',
  // Extra identifiers / flags — opt-in via the column menu.
  'public_id',
  'branchio_link',
  'mytrips_display',
  'exclude_from_chat',
  'exclude_from_group_chat',
  'integration',
  'affiliate_ext_id',
  'start_ts',
  // The trip columns (D5) — opt-in.
  'end_ts',
  'end_ts_type',
  'timezone',
  'duration_s',
  'phase',
  'participant_kind',
  // The expansion columns: hidden, so a default view asks for no `expand=`.
  'guides',
  'start_place',
  'route',
  'guide_app',
  'chat',
  // The combined Timestamps column already shows both sources in brackets, so
  // the standalone source columns are opt-in.
  'create_source_display',
  'update_source_display',
  // The combined Timestamps column is the visible default; the raw sortable
  // Created/Updated columns are opt-in.
  'created_ts',
  'updated_ts',
];

const EMBEDDED_DEFAULT_HIDDEN = [...DEFAULT_HIDDEN, 'guest'];

const NO_TENANTS: TenantPickerValue[] = [];

/** Presets store the bookings filter state as the raw `?q=` DSL text — it IS
 *  the canonical form (round-trips through parse/serialize). */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        // A group that collapsed to one child is no longer canonical — inline it.
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

/** Whether a top-level tree node is a `<ns>=<value>` (iexact) condition —
 *  case-insensitively, matching the backend's own iexact semantics. Only
 *  inspects the FLAT top level (not inside nested groups) — clicking a cell
 *  value is meant to toggle a simple top-level AND filter, not reach into
 *  whatever grouping the user may have typed by hand. */
function isValueCond(n: FilterNode, ns: string, value: string): boolean {
  return (
    n.kind === 'cond' &&
    n.namespace === ns &&
    typeof n.value === 'string' &&
    n.value.toLowerCase() === value.toLowerCase()
  );
}

export interface BookingsPageProps {
  /** Embedded mode: pin every request to this user's bookings and drop the
   *  page furniture (header, tenant picker, presets). Used by the user detail
   *  page's Bookings tab. The pin is composed into the wire filters, never left
   *  to the query widget -- `lockedFilters` is UI-only. */
  fixedUserId?: number;
}

export function BookingsPage({ fixedUserId }: BookingsPageProps = {}) {
  const embedded = fixedUserId !== undefined;
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildBookingNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  // Embedded there is no ?tenant= to out-rank the console scope, so an AppBar
  // scope on another operator would silently empty the tab; suppress it and let
  // a guest's bookings show across tenants.
  const pageTenant = usePageTenant(
    embedded ? { multiple: true, suppressScope: true } : { multiple: true },
  );
  // Drives the preset defaults' tenant COLUMN only; the filtering value is
  // resolved below, once ?q= has been parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  // Base column defs — order/visibility applied on top by the presets hook.
  // (Callbacks are wired below; the columns memo lives further down.)

  const presets = useGridPresets({
    grid: 'bookings',
    // The tenant column exists unless exactly one tenant is picked;
    // reconciliation in the hook tolerates it appearing/disappearing.
    defaultOrder: useMemo(
      () => buildColumns({ showTenant: defaultShowTenant }).map((c) => c.field),
      [defaultShowTenant],
    ),
    // Embedded on a user's record every row is that user's booking, so the Guest
    // column would repeat the page's own subject on every line.
    defaultHidden: embedded ? EMBEDDED_DEFAULT_HIDDEN : DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
    // The tab must not fight the host page for ?q= / ?preset= / ?cols=.
    enabled: !embedded,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  // Parse the URL query into a tree (tolerant — hand-edited URLs are valid).
  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  // Surface parse diagnostics once per distinct query (hand-edited-URL case).
  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;
  // The single-tenant predicate (D5): batch value pickers and the cross-tenant
  // heuristic treat several tenants like all tenants.
  const gridTenantId = singleTenantId(tenantApiValue);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    // Embedded tab grids stay small (components.md "Height model") -- this grid
    // keeps its own virtualized scroller, but 50-row pages inside a tab make
    // the host page scroll twice.
    pageSize: embedded ? EMBEDDED_PAGE_SIZE : DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  // Reset to page 0 whenever the query or tenant changes.
  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  // When tenants are picked and the tree still carries tenant conditions,
  // strip them and rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      // Editing the query diverges a loaded preset into custom mode.
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const onTenantChange = pageTenant.setTenants;

  // The embedded pin. `exact` on `user` is the backend's match-by-PK; it is
  // resolved through the namespace config rather than hardcoded, because an
  // operator the registry doesn't declare 400s at runtime. It is deliberately
  // NOT round-tripped through ?q= -- the DSL serializes `exact` and `iexact`
  // identically, so a URL-resident pin would re-parse as `iexact`.
  const pinnedUserFilter = useMemo<WireNode | null>(() => {
    if (fixedUserId === undefined) return null;
    const operator = resolveConditionOperator(namespaces.user?.operators, 'exact');
    if (operator === null) return null;
    return { namespace: 'user', operator, value: String(fixedUserId) };
  }, [fixedUserId, namespaces]);

  // ONE wire AST for every consumer -- the list, the count-only call and the
  // batch all_matching query string. Composing the pin here rather than at each
  // call site is what stops a batch action escaping to every booking in the
  // system if one site is ever missed.
  const wireFilters = useMemo(() => {
    const wire = toWireAst(tree);
    return pinnedUserFilter ? [pinnedUserFilter, ...wire] : wire;
  }, [tree, pinnedUserFilter]);

  const lockedFilters = {
    ...(tenantApiValue ? { tenant: { operator: 'iexact', value: tenantApiValue } } : {}),
    // Removes `user` from the widget's namespace menu and refuses it when typed
    // or pasted, so the tab cannot be widened past this user.
    ...(fixedUserId !== undefined
      ? { user: { operator: 'exact', value: String(fixedUserId) } }
      : {}),
  };
  // The bookings autocompletes take the list too.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Row-total mode, part 1: the persisted override. Read before the query
  // because it belongs in the query key — switching modes must re-issue the
  // request. Part 2 (useCountMode) needs the response and comes after.
  const countPref = useGridCountMode('bookings');

  // The filter identity of the current query, shared by the list call and the
  // count-only call so the total can never describe a different set than the
  // rows on screen.
  const countOnlyParams = useMemo(
    () => ({
      ordering,
      ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
      filters: wireFilters,
    }),
    [ordering, tenantApiValue, wireFilters],
  );

  // Row expansions (`?expand=`) follow the visible columns: each one costs the
  // backend a batched resolver per page, so only what is on screen is asked for.
  // Showing or hiding such a column re-fetches; the rows stay while it loads.
  // Not part of the filter identity -- the count and a batch's all-matching
  // query never carry it.
  const expand = useMemo(
    () => expansionsForColumns(presets.columnVisibilityModel),
    [presets.columnVisibilityModel],
  );
  const expandKey = expand.join(',');

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'bookings' });
  const query = useQuery({
    queryKey: [
      'bookings',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      fixedUserId ?? null,
      q,
      extendedTimeout,
      countPref.mode,
      expandKey,
    ],
    queryFn: () =>
      bookingsApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        filters: wireFilters,
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        // Only when the user overrode the mode; omitting it keeps the backend's
        // own (deferred) default, which is what makes a cold load free.
        ...(countPref.mode ? { count_mode: countPref.mode } : {}),
        ...(expand.length > 0 ? { expand } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    // While ?preset= is set but its settings haven't loaded, the effective
    // filters/ordering aren't known yet — don't fire a default-state fetch.
    enabled: !presets.blocking,
  });

  // -------------------------------------------------------------------------
  // Batch operations (export / change owner / add-remove tags). This grid is a
  // raw DataGridPro (not ResourceDataGrid), so the batch layer is wired here by
  // hand, mirroring ResourceDataGrid: controlled row selection +
  // keepNonExistentRowsSelected, an all-matching mode, a SelectAllBanner, the
  // BatchActionsMenu (via the toolbar slot), and useBatchAction('bookings').
  // -------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  // Reset the all-matching flag whenever the underlying query changes (the
  // "matching" set is no longer what the user opted into). Mirrors
  // ResourceDataGrid's queryIdentity tracking; here the query key already
  // encodes tenant + q + ordering + page size.
  const queryIdentity = useMemo(
    () =>
      JSON.stringify({
        q,
        tenant: tenantApiValue,
        ordering,
        pageSize: pagination.pageSize,
        user: fixedUserId ?? null,
      }),
    [q, tenantApiValue, ordering, pagination.pageSize, fixedUserId],
  );

  // Row totals. This grid defers its count server-side, so `count` arrives as
  // -1 and the pager renders "Show total" instead of a number; MUI paginates on
  // `paginationMeta.hasNextPage`. See components/DataGrid/useCountMode.ts.
  const count = useCountMode({
    pref: countPref,
    queryIdentity,
    response: query.data,
    countOnly: () => bookingsApi.count(countOnlyParams),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(query.data ? { rowsOnPage: query.data.results.length } : {}),
    onResetPage: () => setPagination((p) => ({ ...p, page: 0 })),
  });
  const rowCount = count.rowCount;
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(
    () => selectionModel.map((i) => Number(i)).filter(Number.isFinite),
    [selectionModel],
  );
  const selectedCount = allMatching ? (count.total ?? selectedIds.length) : selectedIds.length;

  // Tenant id per selected row — feeds detectCrossTenant's ids-mode heuristic.
  // keepNonExistentRowsSelected can retain ids for rows not on the current page,
  // so a missing row contributes `null` (unknown → the server stays authoritative).
  const selectedRowTenantIds = useMemo(() => {
    const byId = new Map(rows.map((r) => [r.id, r.tenant?.id ?? null]));
    return selectedIds.map((id) => byId.get(id) ?? null);
  }, [rows, selectedIds]);

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  // The list query string the server must re-run for all_matching — the SAME
  // params bookingsApi.list sends (ordering + tour_operator_id + filters), so
  // the batch resolves the identical set the grid shows.
  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    if (wireFilters.length > 0) usp.set('filters', JSON.stringify(wireFilters));
    return usp.toString();
  }, [ordering, tenantApiValue, wireFilters]);

  const batchAction = useBatchAction({
    resource: 'bookings',
    queryKey: ['bookings'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  // Declared unconditionally (batch-gating lesson, 2026-07-24): each action
  // carries its own `permission`; the menu filters, the backend re-checks. A
  // page-level `canManage` gate would hide the checkbox column from
  // export-only operators.
  const batchActions = useMemo<BatchActionDef[]>(() => {
    return [
      entityExportBatchAction({
        permission: 'console_bookings_export',
        entity: 'booking',
        resource: 'bookings',
        noun: 'booking(s)',
      }),
      {
        key: 'change_owner',
        label: 'Change owner',
        action: 'change_owner',
        permission: 'console_bookings_manage',
        dialog: (ctx) => (
          <ChangeOwnerDialog
            ctx={ctx}
            gridTenantId={gridTenantId}
            selectedRowTenantIds={selectedRowTenantIds}
          />
        ),
      },
      {
        key: 'manage_tags',
        label: 'Manage tags',
        action: 'manage_tags',
        permission: 'console_bookings_manage',
        dialog: (ctx) => (
          <ManageTagsDialog
            ctx={ctx}
            gridTenantId={gridTenantId}
            selectedRowTenantIds={selectedRowTenantIds}
          />
        ),
      },
      {
        key: 'reimport_xml',
        label: 'Reimport from stored XML',
        action: 'reimport_xml',
        // Its own permission, not console_bookings_manage: this one can delete
        // bookings before replaying them.
        permission: 'console_bookings_reimport',
        dialog: (ctx) => <ReimportXmlDialog ctx={ctx} />,
      },
      {
        key: 'edit_fields',
        label: 'Edit bookings',
        action: 'edit_fields',
        // Ops Admin only: moves, cancels and re-keys bookings in bulk.
        permission: 'console_bookings_edit',
        dialog: (ctx) => <EditBookingsDialog ctx={ctx} />,
      },
      {
        key: 'emit_event',
        label: 'Emit event',
        action: 'emit_event',
        // Ops Admin only: sends real notifications to guests in bulk.
        permission: 'console_events_emit',
        dialog: (ctx) => <EmitEventDialog ctx={ctx} grid="bookings" />,
      },
    ];
  }, [gridTenantId, selectedRowTenantIds]);

  const batchEnabled = batchActions.length > 0;

  const isTagFilterActive = useCallback(
    (tag: BookingTag) => tree.some((n) => isValueCond(n, 'tag', tag.name)),
    [tree],
  );

  // All cell-driven filter edits mutate the SAME tree the QuerySearchInput
  // widget above owns — pushing them through `setQuery` (not some separate
  // state) is what makes the widget's own chip row update too, via its
  // existing external-value-change effect.

  /** The single gate between cell actions and the query tree: coerce the
   *  requested operator to one the namespace config declares (the config is
   *  contract-tested against the backend registry), so a cell can never
   *  emit a condition the backend rejects with "Operator 'X' is not valid
   *  for namespace 'Y'". Null = the namespace supports no equivalent match. */
  const declaredOperator = useCallback(
    (ns: string, preferred: QueryOperator): QueryOperator | null => {
      const op = resolveConditionOperator(namespaces[ns]?.operators, preferred);
      if (op === null) {
        snackbar.show(`Filtering by "${ns}" is not supported here.`, 'warning');
      }
      return op;
    },
    [namespaces, snackbar],
  );

  /** Add or remove one top-level `<ns>=<value>` condition (Tags column via
   *  toggleTagFilter, Departure/State cells directly). */
  const toggleValueFilter = useCallback(
    (ns: string, value: string) => {
      const idx = tree.findIndex((n) => isValueCond(n, ns, value));
      if (idx >= 0) {
        setQuery(tree.filter((_, i) => i !== idx));
        return;
      }
      const operator = declaredOperator(ns, 'iexact');
      if (operator === null) return;
      setQuery([...tree, { kind: 'cond', namespace: ns, operator, value }]);
    },
    [tree, setQuery, declaredOperator],
  );

  const toggleTagFilter = useCallback(
    (tag: BookingTag) => toggleValueFilter('tag', tag.name),
    [toggleValueFilter],
  );

  const toggleCellFilter = useCallback(
    (ns: ToggleableNamespace, value: string) => toggleValueFilter(ns, value),
    [toggleValueFilter],
  );

  /** Whether a toggling cell action's condition is currently in the query —
   *  drives the on/off wording of the Departure and Access-groups popovers. */
  const isCellFilterActive = useCallback(
    (ns: ToggleableNamespace, value: string) => tree.some((n) => isValueCond(n, ns, value)),
    [tree],
  );

  // ---- Manifest ----------------------------------------------------------
  // The set of bookings a guide's sheet for one run would list: same tenant,
  // tour, date and departure, booked only. Every namespace involved is
  // single-valued per booking, so applying a manifest REPLACES those
  // namespaces' conditions rather than ANDing more of them on.

  /** The conditions one row's manifest is made of. `tenant` is omitted when the
   *  tenant PICKER owns that filter (one tenant or several) — the page's own
   *  effect strips tenant conditions in that case, and adding one here would
   *  only flash a snackbar. The tour id pins the tenant anyway. */
  const manifestConds = useCallback(
    (row: BookingListItem): FilterNode[] => {
      const out: FilterNode[] = [];
      const push = (ns: string, preferred: QueryOperator, value: FilterValue) => {
        const operator = declaredOperator(ns, preferred);
        if (operator === null) return;
        out.push({ kind: 'cond', namespace: ns, operator, value });
      };
      if (!tenantApiValue && row.tenant?.code) push('tenant', 'iexact', row.tenant.code);
      // The tour ID, not its name — the backend's all-digits fallback pins THIS
      // tour rather than every same-named one (same trick as the Tour cell).
      if (row.tour) push('tour', 'iexact', String(row.tour.id));
      if (row.date) push('date', 'exact', row.date);
      if (row.departure) push('departure', 'iexact', row.departure.name);
      push('state', 'iexact', BOOKED_STATE_LABEL);
      return out;
    },
    [tenantApiValue, declaredOperator],
  );

  const isManifestActive = useCallback(
    (row: BookingListItem) => {
      const conds = manifestConds(row);
      return conds.every(
        (c) =>
          c.kind === 'cond' &&
          typeof c.value === 'string' &&
          tree.some((n) => isValueCond(n, c.namespace, c.value as string)),
      );
    },
    [manifestConds, tree],
  );

  const toggleManifest = useCallback(
    (row: BookingListItem) => {
      const conds = manifestConds(row);
      const touched = new Set(conds.map((c) => (c.kind === 'cond' ? c.namespace : '')));
      const kept = tree.filter((n) => !(n.kind === 'cond' && touched.has(n.namespace)));
      setQuery(isManifestActive(row) ? kept : [...kept, ...conds]);
    },
    [manifestConds, tree, isManifestActive, setQuery],
  );

  // Date shortcuts, the Tour filter and the Timestamps ±deltas REPLACE (not
  // stack) the single top-level condition of their namespace — a booking has
  // one date/tour/created_ts, so two such conditions ANDed together would
  // almost always be an empty result. `null` removes the condition (reset).
  const { setTenants, fromScope } = pageTenant;
  // Read at click time, so a data refresh doesn't rebuild the columns.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const replaceFilter = useCallback(
    (ns: ReplaceableNamespace, cond: ReplaceFilterCond | null) => {
      // Several PICKED tenants show the Tenant column. Its "Filter by this
      // tenant" then replaces the pick (a `tenant=` condition would only be
      // stripped back out by the effect above), and its reset clears the pick.
      if (ns === 'tenant' && tenantApiValue && !fromScope) {
        if (!cond) {
          setTenants([]);
          return;
        }
        const picked = rowsRef.current.find((r) => r.tenant?.code === cond.value)?.tenant;
        if (picked) {
          setTenants([{ id: picked.id, name: picked.name, code: picked.code }]);
          return;
        }
      }
      const kept = tree.filter((n) => !(n.kind === 'cond' && n.namespace === ns));
      if (cond) {
        const operator = declaredOperator(ns, cond.operator);
        if (operator === null) return;
        setQuery([...kept, { kind: 'cond', namespace: ns, operator, value: cond.value }]);
      } else if (kept.length !== tree.length) {
        setQuery(kept);
      }
    },
    [tree, setQuery, declaredOperator, tenantApiValue, fromScope, setTenants],
  );

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({
          // Shown unless exactly one tenant is picked: several tenants' rows mix.
          showTenant: tenants.length !== 1,
          onToggleTag: toggleTagFilter,
          isTagActive: isTagFilterActive,
          onReplaceFilter: replaceFilter,
          onToggleFilter: toggleCellFilter,
          isFilterActive: isCellFilterActive,
          onToggleManifest: toggleManifest,
          isManifestActive,
        }),
        presets.orderedFields,
      ),
    [
      tenants.length,
      toggleTagFilter,
      isTagFilterActive,
      replaceFilter,
      toggleCellFilter,
      isCellFilterActive,
      toggleManifest,
      isManifestActive,
      presets.orderedFields,
    ],
  );

  // Master-detail: expanding a row shows a compact, single-group summary of the
  // most important properties (the full view lives on the /bookings/:id page).
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<BookingListItem>) => (
      <Box sx={{ p: 1.5 }}>
        <BookingPanelSummary bookingId={row.id} />
      </Box>
    ),
    [],
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    // Embedded, the host page owns the scroll column, so don't claim it here.
    <Box sx={embedded ? undefined : PAGE_FILL_SX}>
      {!embedded && (
        <PageHeader
          helpStory="bookings-grid"
          title="Bookings"
          subtitle="Search bookings across every tenant with a tokenized query."
        />
      )}

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        {/* No tenant picker embedded: a guest's bookings may span operators,
            and the pinned user is the scope that matters. */}
        {!embedded && (
          <Box sx={{ width: 280, flexShrink: 0 }}>
            <TenantPicker multiple value={tenants} onChange={onTenantChange} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(Object.keys(lockedFilters).length ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search bookings…  e.g. tag=~mariott & (departure=08:00 | departure=09:00)"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DataGridPro<BookingListItem>
            apiRef={apiRef}
            rows={rows}
            rowCount={rowCount}
            {...(count.paginationMeta ? { paginationMeta: count.paginationMeta } : {})}
            getRowId={(r) => r.id}
            columns={columns}
            loading={query.isLoading || query.isFetching}
            sx={embedded ? { minHeight: 360 } : { flex: 1, minHeight: 360 }}
            getRowHeight={() => 'auto'}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            // DataGridPro does NOT paginate by default (unlike the community
            // grid); without `pagination` it renders a single page with no
            // pager footer, so page/page-size controls never appear.
            pagination
            paginationMode="server"
            sortingMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            sortModel={presets.sortModel}
            onSortModelChange={presets.onSortModelChange}
            columnVisibilityModel={presets.columnVisibilityModel}
            onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
            onColumnOrderChange={() =>
              presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))
            }
            pageSizeOptions={[25, 50, 100, 200]}
            checkboxSelection={batchEnabled}
            disableRowSelectionOnClick
            disableColumnFilter
            {...(batchEnabled
              ? {
                  rowSelectionModel: selectionModel,
                  onRowSelectionModelChange: handleSelectionChange,
                  keepNonExistentRowsSelected: true,
                }
              : {})}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  {hasSearch ? 'No bookings match this search.' : 'Search to find bookings.'}
                </Box>
              ),
              toolbar: GridControlsToolbar as GridSlots['toolbar'],
            }}
            slotProps={{
              loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
              pagination: {
                labelDisplayedRows: countLabelDisplayedRows({
                  state: count.state,
                  total: count.total,
                  canRequest: true,
                  onRequest: count.requestTotal,
                }),
              },
              toolbar: {
                refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
                hideFilterButton: true,
                exportFileName: 'bookings',
                presets: presets.toolbar,
                countMode: { mode: count.mode, setMode: count.setMode },
                ...(batchEnabled
                  ? {
                      batch: {
                        actions: batchActions,
                        ids: selectedIds,
                        allMatching,
                        count: selectedCount,
                        matched: count.total,
                        filterQuery,
                        tenantFilterId: gridTenantId,
                        rowTenantIds: selectedRowTenantIds,
                        runStandard,
                        clearSelection,
                        onSelectAllMatching: () => setAllMatching(true),
                        isPending: batchAction.isPending,
                      },
                    }
                  : {}),
              } as GridToolbarProps,
            }}
          />
        </>
      )}

      {query.data?.count_is_capped && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          10,000+ results — refine your search to see more precise counts.
        </Typography>
      )}
    </Box>
  );
}
